import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const integrations = [
  {
    icon: 'ri-building-2-line',
    title: 'Into Storage',
    description: 'Processed, frozen SKUs move directly into Freshhlock Storage — no re-handling, no re-labelling. The SKU created at processing is the SKU stored.',
    href: '/storage',
    color: 'bg-secondary-100 text-secondary-600',
    border: 'border-secondary-200',
  },
  {
    icon: 'ri-truck-line',
    title: 'Into Transport',
    description: 'Standardised pack sizes mean optimal reefer space utilisation. Processed packs stack efficiently — more product per trip, lower cost per kilo.',
    href: '/transport',
    color: 'bg-primary-100 text-primary-600',
    border: 'border-primary-200',
  },
  {
    icon: 'ri-exchange-line',
    title: 'Into Wholesale',
    description: 'Processing-generated SKUs are immediately tradeable on the wholesale marketplace. Buyers order by SKU code with full spec confidence.',
    href: '/wholesale',
    color: 'bg-accent-100 text-accent-600',
    border: 'border-accent-200',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Into Retail',
    description: 'Vacuum-packed, retail-ready packs with consumer labels flow directly into the B2C storefront — consistent cut, weight, and presentation every time.',
    href: '/retail',
    color: 'bg-foreground-100 text-foreground-600',
    border: 'border-foreground-200',
  },
];

export default function ProcessingIntegration() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-20 md:py-28 warm-sand-bg">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-4">
            Network Integration
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
            Processing Feeds the Entire Network
          </h2>
          <p className="text-foreground-500 max-w-xl mx-auto">
            A SKU created in Processing flows seamlessly into Storage, Transport, Wholesale, and Retail — one standard, end to end.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {integrations.map((item) => (
            <Link
              key={item.title}
              to={item.href}
              className={`group relative bg-card-50 rounded-xl p-6 md:p-7 border ${item.border} shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer card-animate`}
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${item.color} mb-5 animate-icon-float icon-hover-pop`}>
                <i className={`${item.icon} text-xl`}></i>
              </div>

              <h3 className="font-heading font-bold text-primary-700 text-base mb-2">
                {item.title}
              </h3>

              <p className="text-sm text-foreground-500 leading-relaxed">
                {item.description}
              </p>
            </Link>
          ))}
        </div>

        {/* Visual flow bar */}
        <div className="mt-12 flex items-center justify-center gap-2 flex-wrap">
          <div className="px-4 py-2 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold">
            Processing
          </div>
          <i className="ri-arrow-right-line text-foreground-300"></i>
          <div className="px-4 py-2 rounded-full bg-secondary-100 text-secondary-600 text-sm font-semibold">
            Storage
          </div>
          <i className="ri-arrow-right-line text-foreground-300"></i>
          <div className="px-4 py-2 rounded-full bg-primary-100 text-primary-600 text-sm font-semibold">
            Transport
          </div>
          <i className="ri-arrow-right-line text-foreground-300"></i>
          <div className="px-4 py-2 rounded-full bg-accent-100 text-accent-600 text-sm font-semibold">
            Wholesale
          </div>
          <i className="ri-arrow-right-line text-foreground-300"></i>
          <div className="px-4 py-2 rounded-full bg-foreground-100 text-foreground-700 text-sm font-semibold">
            Retail
          </div>
        </div>
      </div>
    </section>
  );
}